import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../components/icons';
import { Callout } from '../components/ui';

/** What reading invoice photos by AI means (D11, and turning it on in D1). */
export function AiPhotosText() {
  return (
    <>
      <p className="sheet__text">
        <Trans i18nKey="photos.consentText" components={{ strong: <strong /> }} />
      </p>
      <Callout icon="shield-check">
        <Trans i18nKey="photos.consentNote" />
      </Callout>
    </>
  );
}

/** A short list of points (what's in the download, what deleting does, what's new in the notice). */
export function Points({ items }: { items: ReactNode[] }) {
  return (
    <ul className="points">
      {items.map((item, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a fixed list of sentences
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

/** Label and value rows (D2's "You" and "Kept for you", D10's Grievance Officer). */
export function Facts({ rows }: { rows: { label: string; value: ReactNode; sub?: string | null }[] }) {
  return (
    <dl className="facts">
      {rows.map((row) => (
        <div key={row.label} className="facts__row">
          <dt>
            {row.label}
            {row.sub ? <span className="facts__sub">{row.sub}</span> : null}
          </dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A centred screen of its own (D6, D7): a tile, a title and a few sentences. */
export function Centre({
  icon,
  tone,
  title,
  titleId,
  children,
}: {
  icon: IconName;
  tone: 'saffron' | 'red' | 'navy';
  title: string;
  titleId: string;
  children: ReactNode;
}) {
  return (
    <div className="centre">
      <span className={`centre__tile centre__tile--${tone}`}>
        <Icon name={icon} size={28} />
      </span>
      <h1 className="centre__title" id={titleId}>
        {title}
      </h1>
      {children}
    </div>
  );
}

/** "3.2 MB" / "820 KB". */
export function useFileSize() {
  const { i18n } = useTranslation();
  return (bytes: number) => {
    const locale = i18n.language === 'hi' ? 'hi-IN' : 'en-IN';
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toLocaleString(locale, { maximumFractionDigits: 1 })} MB`;
    }
    return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString(locale)} KB`;
  };
}
